import { useAsesor } from "@/hooks/useAsesor";
import { MOVEMENT_META } from "@/pages/admin/consignaciones/components/movementMeta";
import { formatCOP, formatDate, formatNumber } from "@/utils/format";

// Ficha del asesor (solo lectura, en su propio portal): resume sus ventas
// realizadas, lo que tiene en tenencia, lo que debe y su margen / ganancia.
const AdvisorFicha = () => {
  const {
    name,
    margin,
    sales,
    movements,
    consignedUnits,
    consignadoValue,
    debt,
    soldConsignedUnits,
    soldConsignedValue,
    profitRealized,
    potentialProfit,
  } = useAsesor();

  const cards = [
    {
      icon: "ri-checkbox-circle-line",
      label: "Ventas realizadas",
      value: `${formatNumber(soldConsignedUnits)} und`,
      hint: `${formatCOP(soldConsignedValue)} colocados a clientes`,
      bubble: "bg-accent-500/20 text-accent-200",
    },
    {
      icon: "ri-hand-heart-line",
      label: "Tenencia (en consignación)",
      value: `${formatNumber(consignedUnits)} und`,
      hint: `${formatCOP(consignadoValue)} en custodia · no es deuda`,
      bubble: "bg-secondary-500/25 text-secondary-200",
    },
    {
      icon: "ri-hand-coin-line",
      label: "Deuda con Juanka",
      value: formatCOP(debt),
      hint: debt > 0 ? "Lo que debes por lo que vendiste" : "Sin saldo pendiente",
      bubble: "bg-primary-500/25 text-primary-100",
    },
    {
      icon: "ri-percent-line",
      label: "Tu margen comercial",
      value: `${margin}%`,
      hint: "tu descuento sobre el precio de catálogo",
      bubble: "bg-accent-500/15 text-accent-100",
    },
    {
      icon: "ri-line-chart-line",
      label: "Ganancia realizada",
      value: formatCOP(profitRealized),
      hint: `${formatNumber(sales.length)} ventas registradas`,
      bubble: "bg-primary-500/20 text-primary-100",
    },
    {
      icon: "ri-money-dollar-circle-line",
      label: "Ganancia potencial",
      value: formatCOP(potentialProfit),
      hint: "si vendes toda tu mercancía actual",
      bubble: "bg-secondary-500/20 text-secondary-100",
    },
  ];

  return (
    <div className="space-y-5">
      <div className="relative overflow-hidden rounded-lg glass-panel border-accent-500/25">
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-accent-500/10 blur-2xl" />
        <div className="relative px-5 md:px-7 py-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-500/20 text-accent-200">
              <i className="ri-id-card-line text-sm" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-accent-300">
              Mi cuenta · Ficha del asesor
            </span>
          </div>
          <h2 className="editable-title text-2xl md:text-3xl font-extrabold tracking-tight">
            {name}
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-accent-500/15 text-accent-200">
              Margen {margin}%
            </span>
            <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-background-200/60 text-foreground-700">
              {formatNumber(sales.length)} ventas registradas
            </span>
            <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-background-200/60 text-foreground-700">
              {formatCOP(soldConsignedValue + profitRealized)} en movimiento
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
        {cards.map((c) => (
          <div key={c.label} className="glass-panel rounded-lg p-4 animate-fade-up">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.bubble}`}>
              <i className={`${c.icon} text-lg`} />
            </span>
            <p className="text-[11px] text-foreground-500 mt-3">{c.label}</p>
            <p className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
              {c.value}
            </p>
            <p className="text-[10px] text-foreground-500 mt-1">{c.hint}</p>
          </div>
        ))}
      </div>

      <div className="glass-panel rounded-lg p-4 md:p-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-8 h-8 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center">
            <i className="ri-information-line" />
          </span>
          <h3 className="editable-title text-base font-bold">Cómo funciona tu cuenta</h3>
        </div>
        <p className="text-[11px] text-foreground-600 leading-relaxed">
          Tu <span className="text-foreground-300">margen del {margin}%</span> es tu descuento sobre
          el precio de catálogo: compras al admin con ese descuento y ganas esa diferencia cuando
          vendes al precio público. La <span className="text-secondary-200">tenencia</span> es la
          mercancía que tienes en consignación (sigue siendo de Juanka, no es deuda). La{" "}
          <span className="text-accent-200">deuda</span> solo nace cuando vendes o compras, y baja
          con tus abonos.
        </p>
      </div>

      <div className="glass-panel rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-accent-500/15 flex items-center gap-2">
          <i className="ri-history-line text-accent-300" />
          <h3 className="editable-title text-sm font-bold">Movimientos de mi cuenta</h3>
        </div>
        {movements.length === 0 ? (
          <p className="px-5 py-8 text-center text-xs text-foreground-500">
            Aún no tienes movimientos registrados.
          </p>
        ) : (
          <ul className="divide-y divide-background-300/25">
            {movements.map((m) => {
              const meta = MOVEMENT_META[m.kind];
              return (
                <li key={m.id} className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-2.5">
                    <span
                      className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center ${meta.badge}`}
                    >
                      <i className={`${meta.icon} text-sm`} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-950 break-words">
                        {m.productName || meta.label}
                      </p>
                      <p className="text-[10px] text-foreground-500 mt-0.5">
                        {meta.label} · {formatDate(m.date)}
                        {m.qty > 0 ? ` · ${formatNumber(m.qty)} und` : ""}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-sm font-bold whitespace-nowrap ${
                      m.kind === "pago" ? "text-accent-200" : "text-foreground-950"
                    }`}
                  >
                    {m.amount > 0 ? formatCOP(m.amount) : "—"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdvisorFicha;